import { MainApi } from 'api/MainApi'
import { CleaningZoneStatus } from 'api/schema'
import { Feature, GeoJsonProperties, Geometry } from 'geojson'
import cloneDeep from 'lodash.clonedeep'
import isEqual from 'lodash.isequal'
import {
  DEFAULT_VIZUALIZATION_SETTINGS,
  MISSION_CALCULATING_STATUSES,
  MISSION_MAP_DRAWING_MODES,
  MISSION_PAGE_MODES,
  MISSION_ZONE_TYPES,
  PROGRESS_TYPES,
  TRANSFER_CALCULATING_STATUSES,
  TRANSFER_TYPE,
} from 'utils/constants'
import {
  ICleaningModeConfig,
  ICleaningZone,
  ICleaningZonePoint,
  IFeature,
  IMission,
  IMissionInfo,
  IPoint,
  IRoute,
  ITransferRoute,
  IVisualizationSettings,
  IZone,
  IZoneTransferRoute,
} from 'utils/interfaces'
import { ApriorMapVersionId } from 'utils/types'
import create from 'zustand'

interface State {
  mode: MISSION_PAGE_MODES
  mapMode: MISSION_MAP_DRAWING_MODES
  missionInfo: IMissionInfo
  name: string
  apriorMapVersionId: ApriorMapVersionId | null
  calculatingStatus: MISSION_CALCULATING_STATUSES
  loadingProgress: PROGRESS_TYPES
  isReadyForCalculation: boolean
  cleaningZones: ICleaningZone[]
  restrictZones: IZone[]
  highlightedFeature: IFeature | undefined
  editingZone: IZone | undefined
  editingStartingPoint: ICleaningZonePoint | undefined
  editingEndingPoint: ICleaningZonePoint | undefined
  visualSettings: IVisualizationSettings
  tempVisualSettings: IVisualizationSettings
  cleaningRoutes: IRoute[]
  cleaningZoneStartingPoints: ICleaningZonePoint[]
  cleaningZoneEndingPoints: ICleaningZonePoint[]
  transferRoutes: IZoneTransferRoute[]
  status: MISSION_CALCULATING_STATUSES
  missionPoints: {
    tempPoint: IPoint | undefined
    startPoint: IPoint | undefined
    finishPoint: IPoint | undefined
    routes: ITransferRoute[]
    editingPoint: IPoint | undefined
  }
  tempZone: IZone | undefined
  tempStartingPoint: ICleaningZonePoint | undefined
  tempEndingPoint: ICleaningZonePoint | undefined
  tempTransfer: ITransferRoute | undefined
  editingTransfer: ITransferRoute | undefined
  highlightedFeatureInList: string | number | undefined
  savingProgress: PROGRESS_TYPES
  showCleaningZones: boolean
  cleaningModeConfig: ICleaningModeConfig | undefined
  setMode: (mode: MISSION_PAGE_MODES) => void
  setMapMode: (mapMode: MISSION_MAP_DRAWING_MODES) => void
  setMissionName: (name: string) => void
  setApriorMapVersionId: (id: ApriorMapVersionId | null) => void

  setCalculatingStatus: (data: MISSION_CALCULATING_STATUSES) => void
  setLoadingProgress: (data: PROGRESS_TYPES) => void
  setIsReadyForCalculation: (data: boolean) => void
  setCleaningZones: (data: ICleaningZone[]) => void
  setRestrictZones: (data: IZone[]) => void
  setHighlightedFeature: (data: IFeature | undefined) => void
  setEditingZone: (data: IZone | undefined) => void
  setEditingStartingPoint: (data: ICleaningZonePoint | undefined) => void
  setEditingEndingPoint: (data: ICleaningZonePoint | undefined) => void
  setVisualSettings: (data: IVisualizationSettings) => void
  setTempVisualSettings: (data: IVisualizationSettings) => void
  deleteRoutes: () => void
  deleteRoute: (id: number) => void
  getCleaningZones: () => ICleaningZone[]
  addCleaningZoneStartingPoint: (data: ICleaningZonePoint) => void
  addCleaningZoneEndingPoint: (data: ICleaningZonePoint) => void
  getEditingZone: () => IZone | undefined
  getEditingStartingPoint: () => ICleaningZonePoint | undefined
  getEditingEndingPoint: () => ICleaningZonePoint | undefined
  editCleaningZone: (data: ICleaningZone) => void
  setMission: (data: IMission) => void
  setTempZone: (data: IZone | undefined) => void
  setTempStartingPoint: (data: ICleaningZonePoint | undefined) => void
  setTempEndingPoint: (data: ICleaningZonePoint | undefined) => void
  setMissionTempPoint: (data: IPoint | undefined) => void
  setMissionStartPoint: (data: IPoint | undefined) => void
  setMissionFinishPoint: (data: IPoint | undefined) => void
  setTempTransfer: (data: ITransferRoute | undefined) => void
  editTransferRoute: (data: IZoneTransferRoute) => void
  setEditingTransfer: (data: ITransferRoute | undefined) => void
  setHighlightedFeatureInList: (data: string | number | undefined) => void
  setMissionEditingPoint: (data: IPoint | undefined) => void
  setSavingZoneProgress: (data: PROGRESS_TYPES) => void
  setMissionTransferRoutes: (data: Array<ITransferRoute>) => void
  editMissionTransfer: (data: ITransferRoute) => void
  createCleaningZone: (
    zone: ICleaningZone,
    startingPoint?: ICleaningZonePoint,
    endingPoint?: ICleaningZonePoint
  ) => void
  editRestrictZone: (data: IZone) => void
  setTransferRoutes: (data: IZoneTransferRoute[]) => void
  setShowCleaningZones: (data: boolean) => void
  deleteMissionTransfer: (transferId: number) => void
  deleteCleaningZoneStartingPoint: (id: number) => void
  deleteCleaningZoneEndingPoint: (id: number) => void
  setZonesStatus: (data: CleaningZoneStatus[]) => void
  createRestrictZone: (zone: IZone) => void
  editChangeCleaningZone: (
    zoneId: number,
    zone: IZone,
    startingPoint?: ICleaningZonePoint,
    endingPoint?: ICleaningZonePoint
  ) => void
  setChangeRestrictZone: (zone: IZone) => void
  fetchCalculationStatus: (missionId: number, refreshMission?: boolean) => void
  deleteCleaningZone: (zone: IZone) => void
  deleteRestrictZone: (zone: IZone) => void
  reorderCleaningZone: (fromZoneId: number, toZoneId: number) => void
  cancelEditingZone: () => void
  createFeature: (evt: { features: Feature[] }) => void
  editFeature: (evt: { features: Feature[] }) => void
  setChangeZone: (zone: IZone) => void
  setChangeEditingTransfer: (route: ITransferRoute) => void
  cancelEditingTransfer: () => void
  editTransfer: () => void
  deleteTransfer: (transferId: number) => void
  deleteStartPoint: () => void
  deleteFinishPoint: () => void
  setEditingPoint: (point: IPoint) => void
  cancelEditingPoint: () => void
  editPoint: (type: TRANSFER_TYPE, point: IPoint | undefined, startPoint?: IPoint, finishPoint?: IPoint) => void
  fetchMission: (missionId: number) => void
  resetMission: () => void
  patchMissionTransfer: () => void
  setCleaningModeConfig: (data: ICleaningModeConfig | undefined) => void
}
const useMissionStore = create<State>((set, get) => ({
  mode: MISSION_PAGE_MODES.VIEW_ZONES,
  mapMode: MISSION_MAP_DRAWING_MODES.VIEW_ZONES,
  missionInfo: {} as IMissionInfo,
  name: '',
  apriorMapVersionId: null,
  calculatingStatus: MISSION_CALCULATING_STATUSES.UNKNOWN,
  loadingProgress: PROGRESS_TYPES.IDLE,
  isReadyForCalculation: false,
  cleaningZones: [],
  restrictZones: [],
  highlightedFeature: undefined,
  editingZone: undefined,
  editingStartingPoint: undefined,
  editingEndingPoint: undefined,
  visualSettings: DEFAULT_VIZUALIZATION_SETTINGS,
  tempVisualSettings: DEFAULT_VIZUALIZATION_SETTINGS,
  cleaningRoutes: [],
  cleaningZoneStartingPoints: [],
  cleaningZoneEndingPoints: [],
  transferRoutes: [],
  status: MISSION_CALCULATING_STATUSES.NEW,
  missionPoints: {
    tempPoint: undefined,
    startPoint: undefined,
    finishPoint: undefined,
    routes: [],
    editingPoint: undefined,
  },
  cleaningModeConfig: undefined,
  tempZone: undefined,
  tempStartingPoint: undefined,
  tempEndingPoint: undefined,
  tempTransfer: undefined,
  editingTransfer: undefined,
  highlightedFeatureInList: undefined,
  savingProgress: PROGRESS_TYPES.IDLE,
  showCleaningZones: true,
  setCleaningModeConfig: (data: ICleaningModeConfig | undefined) => set({ cleaningModeConfig: data }),
  setMode: data => set({ mode: data }),
  setMapMode: data => {
    set({ mapMode: data })
  },
  setMissionInfo: (data: IMissionInfo) => set({ missionInfo: data }),
  setMissionName: (name: string) => {
    if (get().missionInfo) {
      set({ missionInfo: { ...get().missionInfo, name: name } })
    }
  },
  setApriorMapVersionId: (id: ApriorMapVersionId | null) => {
    if (get().missionInfo) {
      set({ missionInfo: { ...get().missionInfo!, apriorMapVersionId: id } })
    }
  },
  setCalculatingStatus: (data: MISSION_CALCULATING_STATUSES) => set({ calculatingStatus: data }),
  setCalculatingStatusZone: (data: MISSION_CALCULATING_STATUSES, id: number) =>
    set({
      cleaningZones: get().cleaningZones.map(zone =>
        zone.properties?.zone_id === id ? { ...zone, properties: { ...zone.properties, status: data } } : zone
      ),
    }),
  setLoadingProgress: (data: PROGRESS_TYPES) => {
    set({ loadingProgress: data })
  },
  setIsReadyForCalculation: (data: boolean) => set({ isReadyForCalculation: data }),
  setCleaningZones: (data: ICleaningZone[]) => set(() => ({ cleaningZones: data })),

  getCleaningZones: () => {
    return get().cleaningZones
  },
  getEditingZone: () => {
    return get().editingZone
  },
  setRestrictZones: (data: IZone[]) => set({ restrictZones: data }),
  setHighlightedFeature: (data: IFeature | undefined) => set({ highlightedFeature: data }),
  setEditingZone: (data: IZone | undefined) => set({ editingZone: data }),
  setEditingStartingPoint: (data: ICleaningZonePoint | undefined) => set({ editingStartingPoint: data }),
  setEditingEndingPoint: (data: ICleaningZonePoint | undefined) => set({ editingEndingPoint: data }),
  getEditingStartingPoint: () => get().editingStartingPoint,
  getEditingEndingPoint: () => get().editingEndingPoint,
  setVisualSettings: (data: IVisualizationSettings) => set({ visualSettings: data }),
  setTempVisualSettings: (data: IVisualizationSettings) => set({ tempVisualSettings: data }),
  deleteRoutes: () => set({ cleaningRoutes: [] }),
  setTempZone: (data: IZone | undefined) => {
    return set({ tempZone: data })
  },
  deleteRoute: (id: number) => {
    return set({ cleaningRoutes: get().cleaningRoutes.filter(route => route.properties?.zone_id !== id) })
  },
  addCleaningZoneStartingPoint: (data: ICleaningZonePoint) => {
    return set({ cleaningZoneStartingPoints: [...get().cleaningZoneStartingPoints, data] })
  },
  addCleaningZoneEndingPoint: (data: ICleaningZonePoint) => {
    return set({ cleaningZoneEndingPoints: [...get().cleaningZoneEndingPoints, data] })
  },
  editCleaningZone: (data: ICleaningZone) => {
    return set({
      cleaningZones: get().cleaningZones.map(zone =>
        zone.properties?.zone_id === data.properties?.zone_id ? data : zone
      ),
    })
  },
  setTempStartingPoint: (data: ICleaningZonePoint | undefined) => {
    return set({ tempStartingPoint: data })
  },
  setTempEndingPoint: (data: ICleaningZonePoint | undefined) => {
    return set({ tempEndingPoint: data })
  },
  setMissionTempPoint: (data: IPoint | undefined) => {
    set({ missionPoints: { ...get().missionPoints, tempPoint: data } })
  },
  setMissionStartPoint: (data: IPoint | undefined) => {
    return set({ missionPoints: { ...get().missionPoints, startPoint: data } })
  },
  setMissionFinishPoint: (data: IPoint | undefined) => {
    return set({ missionPoints: { ...get().missionPoints, finishPoint: data } })
  },
  setTempTransfer: (data: ITransferRoute | undefined) => {
    return set({ tempTransfer: data })
  },
  editTransferRoute: (data: IZoneTransferRoute) => {
    return set({
      transferRoutes: get().transferRoutes.map(transfer =>
        transfer.properties?.id === data.properties?.id ? data : transfer
      ),
    })
  },
  setEditingTransfer: (data: ITransferRoute | undefined) => {
    return set({ editingTransfer: data })
  },
  setHighlightedFeatureInList: (data: string | number | undefined) => {
    return set({ highlightedFeatureInList: data })
  },
  setMissionEditingPoint: (data: IPoint | undefined) => {
    return set({ missionPoints: { ...get().missionPoints, editingPoint: data } })
  },
  setSavingZoneProgress: (data: PROGRESS_TYPES) => {
    return set({ savingProgress: data })
  },
  setMissionTransferRoutes: (data: Array<ITransferRoute>) => {
    return set({ missionPoints: { ...get().missionPoints, routes: data } })
  },
  editRestrictZone: (data: IZone) => {
    set({
      restrictZones: get().restrictZones.map(zone =>
        zone.properties?.zone_id === data.properties?.zone_id ? data : zone
      ),
    })
  },
  setTransferRoutes: (data: IZoneTransferRoute[]) => {
    set({ transferRoutes: data })
  },
  setShowCleaningZones: (data: boolean) => {
    set({ showCleaningZones: data })
  },
  editMissionTransfer: (data: ITransferRoute) => {
    set(state => ({
      missionPoints: {
        ...state.missionPoints,
        routes: state.missionPoints.routes.map(transfer =>
          transfer.properties?.id === data.properties?.id ? data : transfer
        ),
      },
    }))
  },
  deleteMissionTransfer: (transferId: number) => {
    set(state => ({
      missionPoints: {
        ...get().missionPoints,
        routes: state.missionPoints.routes.filter(transfer => transfer.properties?.id !== transferId),
      },
    }))
  },
  deleteCleaningZoneStartingPoint: (id: number) => {
    set(state => ({
      cleaningZoneStartingPoints: state.cleaningZoneStartingPoints.filter(point => point.properties?.zone_id !== id),
    }))
  },
  deleteCleaningZoneEndingPoint: (id: number) => {
    set(state => ({
      cleaningZoneEndingPoints: state.cleaningZoneEndingPoints.filter(point => point.properties?.zone_id !== id),
    }))
  },
  setMission: (data: IMission) => {
    set({
      missionInfo: data.mission,
      visualSettings: data.visualizationSettings,
      tempVisualSettings: data.visualizationSettings,
      cleaningRoutes: data.cleaningRoutes,
      cleaningZones: data.cleaningZones,
      restrictZones: data.restrictZones,
      cleaningZoneStartingPoints: data.cleaningZoneStartingPoints,
      cleaningZoneEndingPoints: data.cleaningZoneEndingPoints,
      transferRoutes: data.transferRoutes,
      isReadyForCalculation: data.isReadyForCalculation,
      status: data.status,
      calculatingStatus: data.status,
      missionPoints: {
        editingPoint: data.editingPoint,
        tempPoint: data.tempPoint,
        startPoint: data.startingPoint,
        finishPoint: data.finishingPoint,
        routes: data.pointRoutes || [],
      },
    })
  },
  setZonesStatus: (data: CleaningZoneStatus[]) => {
    data.forEach(cleaningZoneNewStatus => {
      const cleaningZone = get().cleaningZones.find(zone => zone.properties?.zone_id === cleaningZoneNewStatus.id)
      if (cleaningZone && cleaningZone.properties) {
        cleaningZone.properties.status = cleaningZoneNewStatus.status
          ? (cleaningZoneNewStatus.status as MISSION_CALCULATING_STATUSES)
          : MISSION_CALCULATING_STATUSES.CALCULATING
        cleaningZone.properties.readyForCalculation = cleaningZoneNewStatus.is_ready_to_start_calculation
      }
    })
  },
  resetMission: () => {
    set({
      mode: MISSION_PAGE_MODES.VIEW_ZONES,
      mapMode: MISSION_MAP_DRAWING_MODES.VIEW_ZONES,
      missionInfo: {} as IMissionInfo,
      name: '',
      apriorMapVersionId: null,
      calculatingStatus: MISSION_CALCULATING_STATUSES.UNKNOWN,
      loadingProgress: PROGRESS_TYPES.IDLE,
      isReadyForCalculation: false,
      cleaningZones: [],
      restrictZones: [],
      highlightedFeature: undefined,
      editingZone: undefined,
      editingStartingPoint: undefined,
      editingEndingPoint: undefined,
      visualSettings: DEFAULT_VIZUALIZATION_SETTINGS,
      tempVisualSettings: DEFAULT_VIZUALIZATION_SETTINGS,
      cleaningRoutes: [],
      cleaningZoneStartingPoints: [],
      cleaningZoneEndingPoints: [],
      transferRoutes: [],
      status: MISSION_CALCULATING_STATUSES.NEW,
      missionPoints: {
        tempPoint: undefined,
        startPoint: undefined,
        finishPoint: undefined,
        routes: [],
        editingPoint: undefined,
      },
      tempZone: undefined,
      tempStartingPoint: undefined,
      tempEndingPoint: undefined,
      tempTransfer: undefined,
      editingTransfer: undefined,
      highlightedFeatureInList: undefined,
      savingProgress: PROGRESS_TYPES.IDLE,
      showCleaningZones: true,
      cleaningModeConfig: undefined,
    })
  },
  getStatus: () => {
    return get().calculatingStatus
  },
  createCleaningZone: (zone: ICleaningZone, startingPoint?: ICleaningZonePoint, endingPoint?: ICleaningZonePoint) => {
    const state = get()

    state.setCleaningZones([...state.getCleaningZones(), zone as ICleaningZone])
    state.setCalculatingStatus(MISSION_CALCULATING_STATUSES.NEW)
    state.setTempZone(undefined)
    state.setTempStartingPoint(undefined)
    state.setTempEndingPoint(undefined)
    state.setMode(MISSION_PAGE_MODES.VIEW_ZONES)
    state.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)

    if (startingPoint) {
      state.addCleaningZoneStartingPoint(startingPoint)
    }
    if (endingPoint) {
      state.addCleaningZoneEndingPoint(endingPoint)
    }

    state.setSavingZoneProgress(PROGRESS_TYPES.SUCCESS)
  },
  createRestrictZone: (zone: IZone) => {
    const state = get()
    state.setSavingZoneProgress(PROGRESS_TYPES.WORK)
    state.setRestrictZones([...state.restrictZones, zone])
    state.deleteRoutes()
    state.setCalculatingStatus(MISSION_CALCULATING_STATUSES.NEW)
    state.setTempZone(undefined)
    state.setMode(MISSION_PAGE_MODES.VIEW_ZONES)
    state.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
    state.setSavingZoneProgress(PROGRESS_TYPES.SUCCESS)
  },
  editChangeCleaningZone: (
    zoneId: number,
    zone: IZone,
    startingPoint?: ICleaningZonePoint,
    endingPoint?: ICleaningZonePoint
  ) => {
    const state = get()
    const geometryChanged = !isEqual(zone, state.getEditingZone()?.geometry)
    const startingPointChanged = !isEqual(startingPoint, state.getEditingStartingPoint()?.geometry)
    const endingPointChanged = !isEqual(endingPoint, state.getEditingEndingPoint()?.geometry)
    state.setSavingZoneProgress(PROGRESS_TYPES.WORK)
    if (geometryChanged || startingPointChanged || endingPointChanged) {
      state.deleteRoute(zone.properties?.zone_id as number)
    }
    state.editCleaningZone(zone as ICleaningZone)
    state.deleteCleaningZoneStartingPoint(zoneId)
    state.deleteCleaningZoneEndingPoint(zoneId)
    if (startingPoint) {
      state.addCleaningZoneStartingPoint(startingPoint)
    }
    if (endingPoint) {
      state.addCleaningZoneEndingPoint(endingPoint)
    }
    state.setEditingZone(undefined)
    state.setTempZone(undefined)
    state.setTempStartingPoint(undefined)
    state.setTempEndingPoint(undefined)
    state.setEditingStartingPoint(undefined)
    state.setEditingEndingPoint(undefined)
    state.setMode(MISSION_PAGE_MODES.VIEW_ZONES)
    state.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
    state.setSavingZoneProgress(PROGRESS_TYPES.SUCCESS)
  },
  setChangeRestrictZone: (zone: IZone) => {
    const state = get()
    state.editRestrictZone(zone)
    state.deleteRoutes()
    state.setEditingZone(undefined)
    state.setTempZone(undefined)
    state.setMode(MISSION_PAGE_MODES.VIEW_ZONES)
    state.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
  },
  fetchCalculationStatus: async (missionId, refreshMission) => {
    const state = get()
    try {
      const status = await MainApi.fetchCalculationStatus(missionId)
      if (!status) {
        return
      }
      if (
        status.status !== state.calculatingStatus ||
        (status.status === MISSION_CALCULATING_STATUSES.CALCULATING && refreshMission)
      ) {
        const newMission = await MainApi.fetchMission(state.missionInfo?.id || 0)
        state.setMission(newMission)
        state.setCalculatingStatus(newMission.status)
      }
      if (status.is_ready_to_start_calculation !== state.isReadyForCalculation) {
        state.setIsReadyForCalculation(status.is_ready_to_start_calculation)
      }
      state.setZonesStatus(status.cleaning_zones_statuses)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
    }
  },
  deleteCleaningZone: (cleaningZone: IZone) => {
    const state = get()
    const newZones = state.getCleaningZones().filter(zone_ => zone_.id !== cleaningZone.id)
    state.setCleaningZones(newZones)
    state.setCalculatingStatus(MISSION_CALCULATING_STATUSES.NEW)
    state.deleteRoute(cleaningZone.properties?.zone_id as number)
    state.deleteCleaningZoneStartingPoint(cleaningZone.properties?.zone_id as number)
    state.deleteCleaningZoneEndingPoint(cleaningZone.properties?.zone_id as number)
  },
  reorderCleaningZone: (fromZoneId: number, toZoneId: number) => {
    const zones = get().getCleaningZones()
    const fromIndex = zones.findIndex(zone => zone.properties?.zone_id === fromZoneId)
    const toIndex = zones.findIndex(zone => zone.properties?.zone_id === toZoneId)
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
      return
    }
    const reordered = [...zones]
    ;[reordered[fromIndex], reordered[toIndex]] = [reordered[toIndex], reordered[fromIndex]]
    get().setCleaningZones(reordered)
  },
  deleteRestrictZone: (zone: IZone) => {
    const state = get()
    const newZones = state.restrictZones.filter(zone_ => zone_.id !== zone.id)
    state.setRestrictZones(newZones)
    state.setCalculatingStatus(MISSION_CALCULATING_STATUSES.NEW)
    state.deleteRoutes()
  },
  cancelEditingZone: () => {
    const state = get()
    state.setMode(MISSION_PAGE_MODES.VIEW_ZONES)
    state.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
    state.setTempZone(undefined)
    state.setEditingZone(undefined)
    state.setTempStartingPoint(undefined)
    state.setTempEndingPoint(undefined)
    state.setEditingStartingPoint(undefined)
    state.setEditingEndingPoint(undefined)
  },
  createFeature: async (evt: { features: Feature[] }) => {
    const state = get()
    if (state.mapMode === MISSION_MAP_DRAWING_MODES.DRAW_POLYGON && evt.features[0]) {
      ;(evt.features[0] as Feature<Geometry, GeoJsonProperties>).properties = { name: 'New zone' }
      state.setTempZone(evt.features[0] as IZone)
      state.setTempStartingPoint(undefined)
      state.setTempEndingPoint(undefined)
      state.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE)
    } else if (state.mapMode === MISSION_MAP_DRAWING_MODES.ADD_ZONE_STARTING_POINT && evt.features[0]) {
      state.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE)
      state.setTempStartingPoint(evt.features[0] as ICleaningZonePoint)
    } else if (state.mapMode === MISSION_MAP_DRAWING_MODES.ADD_ZONE_ENDING_POINT && evt.features[0]) {
      state.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE)
      state.setTempEndingPoint(evt.features[0] as ICleaningZonePoint)
    } else if (state.mapMode === MISSION_MAP_DRAWING_MODES.ADD_MISSION_START_POINT && evt.features[0]) {
      try {
        const missionId = state.missionInfo?.id
        if (!missionId) {
          return
        }
        const startPoint = await MainApi.changeMissionStartPoint({
          id: missionId,
          startingPoint: (evt.features[0] as IPoint).geometry,
        })
        if (startPoint) {
          state.setMissionStartPoint(startPoint)
        }
        state.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('Error creating start point')
      }
    } else if (state.mapMode === MISSION_MAP_DRAWING_MODES.ADD_MISSION_FINISH_POINT && evt.features[0]) {
      try {
        const missionId = state.missionInfo?.id
        if (!missionId) {
          return
        }
        const finishPoint = await MainApi.changeMissionFinishPoint({
          id: missionId,
          finishingPoint: (evt.features[0] as IPoint).geometry,
        })
        if (finishPoint) {
          state.setMissionFinishPoint(finishPoint)
        }
        state.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('Error creating finish point')
      }
    }
  },
  editFeature: (evt: { features: Feature[] }) => {
    const state = get()
    if (state.mapMode === MISSION_MAP_DRAWING_MODES.DRAW_POLYGON && evt.features[0]) {
      state.setTempZone(evt.features[0] as IZone)
    } else if (state.mapMode === MISSION_MAP_DRAWING_MODES.ADD_ZONE_STARTING_POINT && evt.features[0]) {
      state.setTempStartingPoint(evt.features[0] as ICleaningZonePoint)
    } else if (state.mapMode === MISSION_MAP_DRAWING_MODES.ADD_ZONE_ENDING_POINT && evt.features[0]) {
      state.setTempEndingPoint(evt.features[0] as ICleaningZonePoint)
    } else if (state.mapMode === MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE) {
      state.setTempZone(evt.features[0] as IZone)
    } else if (state.mapMode === MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_TRANSFER_ROUTE) {
      state.setTempTransfer(evt.features[0] as ITransferRoute)
    } else if (state.mapMode === MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_MISSION_POINT) {
      state.setMissionTempPoint(evt.features[0] as IPoint)
    } else if (state.mapMode === MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_STARTING_POINT) {
      state.setTempStartingPoint(evt.features[0] as ICleaningZonePoint)
    } else if (state.mapMode === MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ENDING_POINT) {
      state.setTempEndingPoint(evt.features[0] as ICleaningZonePoint)
    }
  },
  setChangeZone: (cleaningZone: IZone) => {
    const state = get()
    state.setEditingZone(cleaningZone)
    state.setTempZone(cloneDeep(cleaningZone))
    state.setHighlightedFeature(cleaningZone)
    state.setMode(MISSION_PAGE_MODES.EDIT_ZONE)
    if (cleaningZone.properties.zone_type === MISSION_ZONE_TYPES.CLEANING) {
      const startingPoint = state.cleaningZoneStartingPoints.find(
        point => point.properties?.zone_id === cleaningZone.properties.zone_id
      )
      const endingPoint = state.cleaningZoneEndingPoints.find(
        point => point.properties?.zone_id === cleaningZone.properties.zone_id
      )
      state.setEditingStartingPoint(startingPoint)
      state.setTempStartingPoint(startingPoint)
      state.setEditingEndingPoint(endingPoint)
      state.setTempEndingPoint(endingPoint)
    }
    state.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE)
  },

  setChangeEditingTransfer: (route: ITransferRoute) => {
    const state = get()
    state.setEditingTransfer(route)
    const userGeometryTransfer = cloneDeep(route)
    const tempGeometry = userGeometryTransfer.geometry
    userGeometryTransfer.geometry = cloneDeep(userGeometryTransfer.userGeometry)
    userGeometryTransfer.userGeometry = tempGeometry
    state.setTempTransfer(userGeometryTransfer)
    state.setHighlightedFeature(route)
    state.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_TRANSFER_ROUTE)
  },
  cancelEditingTransfer: () => {
    const state = get()
    state.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
    state.setEditingTransfer(undefined)
    state.setTempTransfer(undefined)
  },
  editTransfer: async () => {
    const state = get()
    try {
      const transferId = state.tempTransfer?.properties.id
      if (transferId) {
        await MainApi.patchTransferRoute(transferId, state.tempTransfer?.geometry)
      }
      const tempTransfer = cloneDeep(state.tempTransfer as IZoneTransferRoute)
      if (tempTransfer) {
        tempTransfer.userGeometry = tempTransfer.geometry
        tempTransfer.properties.status = TRANSFER_CALCULATING_STATUSES.NEW
        state.editTransferRoute(tempTransfer)
      }
      state.setEditingTransfer(undefined)
      state.setTempTransfer(undefined)
      state.setMode(MISSION_PAGE_MODES.VIEW_ZONES)
      state.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error editing cleaning zone', error)
      state.setEditingTransfer(undefined)
      state.setMode(MISSION_PAGE_MODES.VIEW_ZONES)
      state.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
    }
  },
  deleteTransfer: (transferId: number) => {
    const state = get()
    try {
      const newTransferRoutes = state.transferRoutes.filter(route => route.properties.id !== transferId)
      state.setTransferRoutes(newTransferRoutes)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Произошла ошибка:', error)
    }
  },
  deleteStartPoint: () => {
    const state = get()
    state.setMissionStartPoint(undefined)
    state.setMissionTransferRoutes(
      state.missionPoints.routes.filter(route => route.properties.type !== TRANSFER_TYPE.START)
    )
  },
  deleteFinishPoint: () => {
    const state = get()
    state.setMissionFinishPoint(undefined)
    state.setMissionTransferRoutes(
      state.missionPoints.routes.filter(route => route.properties.type !== TRANSFER_TYPE.FINISH)
    )
  },
  setEditingPoint: (point: IPoint) => {
    const state = get()
    state.setMissionEditingPoint(point)
    state.setMissionTempPoint(cloneDeep(point))
    state.setHighlightedFeature(point)
    state.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_MISSION_POINT)
  },
  cancelEditingPoint: () => {
    const state = get()
    state.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
    state.setMissionEditingPoint(undefined)
    state.setMissionTempPoint(undefined)
  },
  editPoint: (type: TRANSFER_TYPE, point: IPoint | undefined, startPoint?: IPoint, finishPoint?: IPoint) => {
    const state = get()
    if (!point) {
      return
    }
    if (type === TRANSFER_TYPE.START) {
      state.setMissionStartPoint(startPoint)
    } else {
      state.setMissionFinishPoint(finishPoint)
    }
    state.setMissionTempPoint(undefined)
    state.setMissionEditingPoint(undefined)
    state.setMode(MISSION_PAGE_MODES.VIEW_ZONES)
    state.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
  },
  fetchMission: async (missionId: number) => {
    const state = get()
    try {
      state.setLoadingProgress(PROGRESS_TYPES.WORK)
      const mission = await MainApi.fetchMission(missionId)
      state.setMission(mission)
      state.setLoadingProgress(PROGRESS_TYPES.SUCCESS)
    } catch (error) {
      state.setLoadingProgress(PROGRESS_TYPES.ERROR)
    }
  },
  patchMissionTransfer: async () => {
    const state = get()
    try {
      const transferId = state.tempTransfer?.properties.id
      if (transferId) {
        await MainApi.patchMissionPointTransferRoute(transferId, state.tempTransfer?.geometry)
      }
      const tempTransfer = cloneDeep(state.tempTransfer as IZoneTransferRoute)
      if (tempTransfer) {
        tempTransfer.userGeometry = tempTransfer.geometry
        tempTransfer.properties.status = TRANSFER_CALCULATING_STATUSES.NEW
        state.editMissionTransfer(tempTransfer)
      }
      state.setEditingTransfer(undefined)
      state.setTempTransfer(undefined)
      state.setMode(MISSION_PAGE_MODES.VIEW_ZONES)
      state.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error editing route')
      state.setEditingTransfer(undefined)
      state.setMode(MISSION_PAGE_MODES.VIEW_ZONES)
      state.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
    }
  },
}))

export default useMissionStore
